import React, { useState, useMemo } from 'react';
import {
    FaArrowUp, FaPlus, FaTrash, FaPen, FaHome, FaChartLine, FaCoins, FaUniversity,
    FaCar, FaBitcoin, FaEllipsisH, FaCog, FaTimes, FaBullseye, FaFire, FaWallet,
    FaCalendarAlt, FaUmbrellaBeach, FaCheck, FaBan, FaPiggyBank
} from 'react-icons/fa';
import {
    AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
    Cell, ReferenceLine
} from 'recharts';
import { addToCollection, updateInCollection, deleteFromCollection } from '../../hooks/useFirestore';

const ASSET_TYPES = [
    { type: 'Real Estate', icon: <FaHome />, color: 'bg-blue-50 text-blue-600' },
    { type: 'Stocks', icon: <FaChartLine />, color: 'bg-emerald-50 text-emerald-600' },
    { type: 'Gold', icon: <FaCoins />, color: 'bg-amber-50 text-amber-600' },
    { type: 'FD', icon: <FaUniversity />, color: 'bg-cyan-50 text-cyan-600' },
    { type: 'Crypto', icon: <FaBitcoin />, color: 'bg-orange-50 text-orange-600' },
    { type: 'Vehicle', icon: <FaCar />, color: 'bg-pink-50 text-pink-600' },
    { type: 'Other', icon: <FaEllipsisH />, color: 'bg-gray-50 text-gray-600' },
];

const INCOME_CATEGORIES = [
    'Salary', 'Freelance', 'Business', 'Investment', 'Rental', 'Dividend', 'Gift', 'Refund', 'Other'
];

const INCOME_CAT_COLORS = {
    Salary: 'bg-blue-100 text-blue-700', Freelance: 'bg-purple-100 text-purple-700',
    Business: 'bg-emerald-100 text-emerald-700', Investment: 'bg-cyan-100 text-cyan-700',
    Rental: 'bg-amber-100 text-amber-700', Dividend: 'bg-teal-100 text-teal-700',
    Gift: 'bg-pink-100 text-pink-700', Refund: 'bg-orange-100 text-orange-700',
    Other: 'bg-gray-100 text-gray-700',
};

const IncomeTab = ({
    L, CHART_COLORS, LIGHT_TOOLTIP,
    assets, totalAssets, totalLiabilities, netWorth,
    transactions, stats, incomeTargets, updateIncomeTargets,
    gamification, calendarMode, selectedDate, dateRange,
    cashInHand, updateCashInHand, holidays,
    fixedExpenses, emis, wishlistItems, operationalExpenses,
}) => {
    const [showAssetForm, setShowAssetForm] = useState(false);
    const [showTargetForm, setShowTargetForm] = useState(false);
    const [showIncomeForm, setShowIncomeForm] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [editIncomeItem, setEditIncomeItem] = useState(null);
    const [editingCash, setEditingCash] = useState(false);
    const [cashInput, setCashInput] = useState('');
    const [showHolidayForm, setShowHolidayForm] = useState(false);
    const [holidayDate, setHolidayDate] = useState(new Date().toISOString().slice(0, 10));
    const [holidayLabel, setHolidayLabel] = useState('');

    // Filter income transactions for selected period
    const periodIncome = useMemo(() => {
        return (transactions || []).filter(t => {
            const isCredit = t.type === 'credit' || t.type === 'income' || t.type === 'gain';
            if (!isCredit) return false;
            const txDate = new Date(t.date);
            return txDate >= dateRange.start && txDate <= dateRange.end;
        }).sort((a, b) => new Date(b.date) - new Date(a.date));
    }, [transactions, dateRange]);

    const totalPeriodIncome = useMemo(() =>
        periodIncome.reduce((s, t) => s + Math.abs(Number(t.amount) || 0), 0),
    [periodIncome]);

    // Savings computation: unallocated income minus operational expenses
    const totalActiveAllocations = useMemo(() =>
        periodIncome.reduce((sum, inc) =>
            sum + (inc.allocations || []).filter(a => a.status !== 'skipped')
                .reduce((s, a) => s + (Number(a.amount) || 0), 0), 0),
    [periodIncome]);

    const periodOperationalTotal = useMemo(() =>
        (operationalExpenses || []).filter(e => {
            const d = new Date(e.date);
            return d >= dateRange.start && d <= dateRange.end;
        }).reduce((s, e) => s + (Number(e.amount) || 0), 0),
    [operationalExpenses, dateRange]);

    const totalSavings = totalPeriodIncome - totalActiveAllocations - periodOperationalTotal;

    // Income trend data
    const trendData = useMemo(() => {
        const ds = {};
        (transactions || []).filter(t => t.type === 'credit' || t.type === 'income' || t.type === 'gain').forEach(t => {
            const d = (t.date || '').slice(0, 10);
            ds[d] = (ds[d] || 0) + Math.abs(Number(t.amount) || 0);
        });

        if (calendarMode === 'month') {
            const start = dateRange.start;
            const end = dateRange.end;
            const points = [];
            const d = new Date(start);
            while (d <= end) {
                const key = d.toISOString().slice(0, 10);
                points.push({ label: String(d.getDate()), income: ds[key] || 0 });
                d.setDate(d.getDate() + 1);
            }
            return points;
        } else if (calendarMode === 'week') {
            const points = [];
            const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
            const d = new Date(dateRange.start);
            for (let i = 0; i < 7; i++) {
                const key = d.toISOString().slice(0, 10);
                points.push({ label: dayNames[d.getDay()], income: ds[key] || 0 });
                d.setDate(d.getDate() + 1);
            }
            return points;
        } else {
            const points = [];
            for (let i = 6; i >= 0; i--) {
                const d = new Date(dateRange.start);
                d.setDate(d.getDate() - i + 3);
                const key = d.toISOString().slice(0, 10);
                points.push({ label: `${d.getDate()}/${d.getMonth() + 1}`, income: ds[key] || 0 });
            }
            return points;
        }
    }, [transactions, calendarMode, dateRange]);

    // Category breakdown
    const categoryData = useMemo(() => {
        const grouped = {};
        periodIncome.forEach(t => {
            const cat = t.category || 'Other';
            grouped[cat] = (grouped[cat] || 0) + Math.abs(Number(t.amount) || 0);
        });
        return Object.entries(grouped).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
    }, [periodIncome]);

    // Income target calculations (holiday-aware)
    const dailyTarget = incomeTargets?.dailyTarget || 0;
    const holidaySet = useMemo(() => new Set((holidays || []).map(h => h.date)), [holidays]);

    // Date ranges for holiday counting (always based on selectedDate)
    const { weekStartDate, monthStartDate, monthEndDate, daysInMonth } = useMemo(() => {
        const ws = new Date(selectedDate);
        const day = ws.getDay();
        const diff = day === 0 ? 6 : day - 1;
        ws.setDate(ws.getDate() - diff);
        ws.setHours(0, 0, 0, 0);
        const we = new Date(ws);
        we.setDate(we.getDate() + 6);
        we.setHours(23, 59, 59, 999);
        const ms = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
        const me = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
        return { weekStartDate: ws, weekEndDate: we, monthStartDate: ms, monthEndDate: me, daysInMonth: me.getDate() };
    }, [selectedDate]);

    const weekHolidays = useMemo(() => {
        let count = 0;
        const d = new Date(weekStartDate);
        for (let i = 0; i < 7; i++) {
            if (holidaySet.has(d.toISOString().slice(0, 10))) count++;
            d.setDate(d.getDate() + 1);
        }
        return count;
    }, [weekStartDate, holidaySet]);

    const monthHolidays = useMemo(() => {
        let count = 0;
        const d = new Date(monthStartDate);
        while (d <= monthEndDate) {
            if (holidaySet.has(d.toISOString().slice(0, 10))) count++;
            d.setDate(d.getDate() + 1);
        }
        return count;
    }, [monthStartDate, monthEndDate, holidaySet]);

    const weeklyTarget = dailyTarget * (7 - weekHolidays);
    const monthlyTarget = dailyTarget * (daysInMonth - monthHolidays);

    const currentTarget = calendarMode === 'day' ? dailyTarget : calendarMode === 'week' ? weeklyTarget : monthlyTarget;
    const targetRemaining = currentTarget - totalPeriodIncome;
    const targetPct = currentTarget > 0 ? (totalPeriodIncome / currentTarget) * 100 : 0;

    // Month holidays list for UI
    const monthHolidayList = useMemo(() => {
        return (holidays || []).filter(h => {
            const d = new Date(h.date + 'T00:00:00');
            return d >= monthStartDate && d <= monthEndDate;
        }).sort((a, b) => a.date.localeCompare(b.date));
    }, [holidays, monthStartDate, monthEndDate]);

    // Holiday CRUD
    const handleAddHoliday = async () => {
        if (!holidayDate) return;
        await addToCollection('holidays', { date: holidayDate, label: holidayLabel || 'Holiday' });
        setHolidayDate(new Date().toISOString().slice(0, 10));
        setHolidayLabel('');
        setShowHolidayForm(false);
    };
    const handleDeleteHoliday = async (id) => { await deleteFromCollection('holidays', id); };

    // Daily income map for streaks
    const dailyIncomeMap = useMemo(() => {
        const map = {};
        (transactions || []).filter(t => t.type === 'credit' || t.type === 'income' || t.type === 'gain').forEach(t => {
            const d = (t.date || '').slice(0, 10);
            map[d] = (map[d] || 0) + Math.abs(Number(t.amount) || 0);
        });
        return map;
    }, [transactions]);

    // Income streak (holidays count as hits — don't break streak)
    const incomeStreak = useMemo(() => {
        if (dailyTarget <= 0) return 0;
        let streak = 0;
        const today = new Date();
        for (let i = 1; i <= 60; i++) {
            const d = new Date(today);
            d.setDate(d.getDate() - i);
            const key = d.toISOString().slice(0, 10);
            if (holidaySet.has(key)) { streak++; continue; }
            if ((dailyIncomeMap[key] || 0) >= dailyTarget) streak++;
            else break;
        }
        return streak;
    }, [dailyIncomeMap, dailyTarget, holidaySet]);

    // Target achievement data (last 14 days, with holiday marking)
    const achievementData = useMemo(() => {
        if (dailyTarget <= 0) return [];
        const points = [];
        const today = new Date();
        for (let i = 13; i >= 0; i--) {
            const d = new Date(today);
            d.setDate(d.getDate() - i);
            const key = d.toISOString().slice(0, 10);
            const earned = dailyIncomeMap[key] || 0;
            const isHoliday = holidaySet.has(key);
            points.push({
                label: `${d.getDate()}/${d.getMonth() + 1}`,
                earned,
                target: dailyTarget,
                hit: isHoliday ? 2 : earned >= dailyTarget ? 1 : 0,
                isHoliday,
            });
        }
        return points;
    }, [dailyIncomeMap, dailyTarget, holidaySet]);

    // Forecast (excludes holidays from remaining working days)
    const forecast = useMemo(() => {
        if (currentTarget <= 0 || totalPeriodIncome >= currentTarget) return null;
        const now = new Date();
        let remainingDays = 0;
        if (calendarMode === 'day') {
            remainingDays = 1;
        } else {
            const periodEnd = dateRange.end;
            const d = new Date(now);
            d.setDate(d.getDate() + 1);
            while (d <= periodEnd) {
                if (!holidaySet.has(d.toISOString().slice(0, 10))) remainingDays++;
                d.setDate(d.getDate() + 1);
            }
        }
        remainingDays = Math.max(1, remainingDays);
        return Math.ceil(targetRemaining / remainingDays);
    }, [currentTarget, totalPeriodIncome, targetRemaining, calendarMode, holidaySet, dateRange]);

    // CRUD
    const handleDeleteAsset = async (id) => { if (window.confirm('Delete this asset?')) await deleteFromCollection('assets', id); };
    const handleDeleteIncome = async (id) => { if (window.confirm('Delete this income entry?')) await deleteFromCollection('transactions', id); };
    const handleAllocationStatus = async (txnId, allocIndex, newStatus, currentAllocations) => {
        const updated = [...currentAllocations];
        updated[allocIndex] = { ...updated[allocIndex], status: newStatus };
        await updateInCollection('transactions', txnId, { allocations: updated });
    };

    return (
        <div className="space-y-6">
            {/* Cash In Hand */}
            <div className={`${L.card} flex items-center justify-between`}>
                <div className="flex items-center gap-4">
                    <div className="w-11 h-11 bg-amber-50 rounded-xl flex items-center justify-center">
                        <FaWallet className="text-amber-500 text-lg" />
                    </div>
                    <div>
                        <p className={L.textLabel}>Cash In Hand</p>
                        {editingCash ? (
                            <div className="flex items-center gap-2 mt-1">
                                <input type="number" autoFocus value={cashInput}
                                    onChange={e => setCashInput(e.target.value)}
                                    onKeyDown={async e => {
                                        if (e.key === 'Enter') { await updateCashInHand({ balance: Number(cashInput) || 0 }); setEditingCash(false); }
                                        if (e.key === 'Escape') setEditingCash(false);
                                    }}
                                    className="w-36 bg-gray-50 border border-blue-300 rounded-lg px-3 py-1.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                                    placeholder="0" />
                                <button onClick={async () => { await updateCashInHand({ balance: Number(cashInput) || 0 }); setEditingCash(false); }}
                                    className="text-xs font-bold text-blue-600 hover:text-blue-700">Save</button>
                                <button onClick={() => setEditingCash(false)}
                                    className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
                            </div>
                        ) : (
                            <p className="text-2xl font-bold text-amber-600 mt-0.5">
                                {'\u20B9'}{(cashInHand?.balance || 0).toLocaleString()}
                            </p>
                        )}
                    </div>
                </div>
                {!editingCash && (
                    <button onClick={() => { setCashInput(String(cashInHand?.balance || '')); setEditingCash(true); }}
                        className={`${L.btnSecondary} flex items-center gap-2`}>
                        <FaPen className="text-[9px]" /> Edit
                    </button>
                )}
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-2 gap-4">
                <div className={L.card}>
                    <p className={L.textLabel}>Total Net Worth</p>
                    <p className={`text-2xl font-bold ${L.accent} mt-1`}>{'\u20B9'}{netWorth.toLocaleString()}</p>
                    <p className={`text-xs ${L.textMuted} mt-1`}>Assets: {'\u20B9'}{totalAssets.toLocaleString()} | Debt: {'\u20B9'}{totalLiabilities.toLocaleString()}</p>
                </div>
                <div className={L.card}>
                    <p className={L.textLabel}>
                        {calendarMode === 'day' ? "Today's" : calendarMode === 'week' ? "This Week's" : "This Month's"} Income
                    </p>
                    <p className={`text-2xl font-bold ${L.positive} mt-1`}>{'\u20B9'}{totalPeriodIncome.toLocaleString()}</p>
                    <p className={`text-xs ${L.textMuted} mt-1`}>{periodIncome.length} transaction{periodIncome.length !== 1 ? 's' : ''}</p>
                </div>
            </div>

            {/* Savings Card */}
            <div className={`${L.card} border ${totalSavings >= 0 ? 'border-emerald-100 bg-emerald-50/30' : 'border-rose-100 bg-rose-50/30'}`}>
                <div className="flex items-center gap-4">
                    <div className={`w-11 h-11 ${totalSavings >= 0 ? 'bg-emerald-50' : 'bg-rose-50'} rounded-xl flex items-center justify-center`}>
                        <FaPiggyBank className={`${totalSavings >= 0 ? 'text-emerald-500' : 'text-rose-500'} text-lg`} />
                    </div>
                    <div className="flex-1">
                        <p className={L.textLabel}>Period Savings (Unallocated)</p>
                        <p className={`text-2xl font-bold ${totalSavings >= 0 ? L.positive : L.negative} mt-0.5`}>
                            {totalSavings < 0 ? '-' : ''}{'\u20B9'}{Math.abs(totalSavings).toLocaleString()}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-4 mt-3 text-[10px] text-gray-400">
                    <span>Income: {'\u20B9'}{totalPeriodIncome.toLocaleString()}</span>
                    <span>{'\u00B7'} Allocated: {'\u20B9'}{totalActiveAllocations.toLocaleString()}</span>
                    <span>{'\u00B7'} Operational: {'\u20B9'}{periodOperationalTotal.toLocaleString()}</span>
                </div>
            </div>

            {/* Income Entries List */}
            <div className={L.card}>
                <div className="flex items-center justify-between mb-5">
                    <div>
                        <h3 className={`text-base font-bold ${L.text}`}>Income Entries</h3>
                        <p className={`text-xs ${L.textMuted} mt-0.5`}>{periodIncome.length} entries for selected period</p>
                    </div>
                    <button onClick={() => { setEditIncomeItem(null); setShowIncomeForm(true); }} className={`${L.btnPrimary} flex items-center gap-2`}>
                        <FaPlus className="text-[10px]" /> Add Income
                    </button>
                </div>
                {periodIncome.length === 0 ? (
                    <div className="text-center py-10">
                        <FaWallet className="text-3xl text-gray-300 mx-auto mb-3" />
                        <p className={`text-sm ${L.textMuted}`}>No income entries for this period</p>
                        <button onClick={() => { setEditIncomeItem(null); setShowIncomeForm(true); }} className={`${L.btnPrimary} mt-3 text-sm`}>Add Your First Income</button>
                    </div>
                ) : (
                    <div className="space-y-2 max-h-[500px] overflow-y-auto">
                        {periodIncome.map(inc => {
                            const catColor = INCOME_CAT_COLORS[inc.category] || INCOME_CAT_COLORS.Other;
                            const hasAllocations = (inc.allocations || []).length > 0;
                            const entryAllocTotal = (inc.allocations || [])
                                .filter(a => a.status !== 'skipped')
                                .reduce((s, a) => s + (Number(a.amount) || 0), 0);
                            const entrySavings = Math.abs(Number(inc.amount)) - entryAllocTotal;
                            return (
                                <div key={inc.id} className="rounded-xl border border-gray-100 group hover:shadow-sm transition-all">
                                    <div className="flex items-center justify-between p-3">
                                        <div className="flex items-center gap-3">
                                            <div className="w-9 h-9 bg-emerald-50 rounded-lg flex items-center justify-center text-emerald-600 text-sm">
                                                <FaArrowUp />
                                            </div>
                                            <div>
                                                <p className={`text-sm font-semibold ${L.text}`}>{inc.description || inc.label || 'Income'}</p>
                                                <div className="flex items-center gap-2 mt-0.5">
                                                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-md ${catColor}`}>{inc.category || 'Other'}</span>
                                                    <span className="text-[10px] text-gray-400">
                                                        {inc.date ? new Date(inc.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <p className={`text-sm font-bold ${L.positive}`}>+{'\u20B9'}{Math.abs(Number(inc.amount)).toLocaleString()}</p>
                                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-all">
                                                <button onClick={() => { setEditIncomeItem(inc); setShowIncomeForm(true); }} className="p-1 text-gray-400 hover:text-blue-600"><FaPen className="text-[9px]" /></button>
                                                <button onClick={() => handleDeleteIncome(inc.id)} className="p-1 text-gray-400 hover:text-rose-500"><FaTrash className="text-[9px]" /></button>
                                            </div>
                                        </div>
                                    </div>
                                    {/* Allocation badges + status toggles */}
                                    {hasAllocations && (
                                        <div className="px-3 pb-3 space-y-1.5 border-t border-gray-50 pt-2">
                                            {inc.allocations.map((alloc, idx) => (
                                                <div key={idx} className="flex items-center justify-between text-[11px] px-2 py-1.5 bg-gray-50 rounded-lg">
                                                    <div className="flex items-center gap-2">
                                                        <button onClick={() => handleAllocationStatus(inc.id, idx, alloc.status === 'paid' ? 'unpaid' : 'paid', inc.allocations)}
                                                            title={alloc.status === 'paid' ? 'Mark unpaid' : 'Mark paid'}
                                                            className={`w-5 h-5 rounded flex items-center justify-center text-[8px] transition-all ${
                                                                alloc.status === 'paid'
                                                                    ? 'bg-emerald-100 text-emerald-600 border border-emerald-200'
                                                                    : 'bg-gray-100 text-gray-400 border border-gray-200 hover:bg-emerald-50 hover:text-emerald-500'
                                                            }`}><FaCheck /></button>
                                                        <button onClick={() => handleAllocationStatus(inc.id, idx, alloc.status === 'skipped' ? 'unpaid' : 'skipped', inc.allocations)}
                                                            title={alloc.status === 'skipped' ? 'Unskip' : 'Skip'}
                                                            className={`w-5 h-5 rounded flex items-center justify-center text-[8px] transition-all ${
                                                                alloc.status === 'skipped'
                                                                    ? 'bg-amber-100 text-amber-600 border border-amber-200'
                                                                    : 'bg-gray-100 text-gray-400 border border-gray-200 hover:bg-amber-50 hover:text-amber-500'
                                                            }`}><FaBan /></button>
                                                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${
                                                            alloc.type === 'fixed' ? 'bg-blue-100 text-blue-700' :
                                                            alloc.type === 'emi' ? 'bg-pink-100 text-pink-700' :
                                                            'bg-indigo-100 text-indigo-700'
                                                        }`}>{alloc.type}</span>
                                                        <span className={`font-medium ${alloc.status === 'skipped' ? 'line-through text-gray-400 italic' : 'text-gray-700'}`}>
                                                            {alloc.itemLabel}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className={`font-semibold ${alloc.status === 'skipped' ? 'text-gray-400' : 'text-gray-700'}`}>
                                                            {'\u20B9'}{Number(alloc.amount).toLocaleString()}
                                                        </span>
                                                        <span className="text-gray-400">
                                                            ({alloc.itemTotal > 0 ? ((alloc.amount / alloc.itemTotal) * 100).toFixed(0) : 0}%)
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                            <div className="flex items-center justify-between text-[11px] px-2 py-1.5 bg-emerald-50 rounded-lg text-emerald-700 font-semibold">
                                                <span>{'\u2192'} Savings</span>
                                                <span>{'\u20B9'}{entrySavings.toLocaleString()}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Asset List */}
            <div className={L.card}>
                <div className="flex items-center justify-between mb-5">
                    <div>
                        <h3 className={`text-base font-bold ${L.text}`}>Assets</h3>
                        <p className={`text-xs ${L.textMuted} mt-0.5`}>{'\u20B9'}{totalAssets.toLocaleString()} total</p>
                    </div>
                    <button onClick={() => { setEditItem(null); setShowAssetForm(true); }} className={`${L.btnPrimary} flex items-center gap-2`}>
                        <FaPlus className="text-[10px]" /> Add Asset
                    </button>
                </div>
                {(!assets || assets.length === 0) ? (
                    <div className="text-center py-10">
                        <FaArrowUp className="text-3xl text-gray-300 mx-auto mb-3" />
                        <p className={`text-sm ${L.textMuted}`}>No assets added yet</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {assets.map(a => {
                            const at = ASSET_TYPES.find(t => t.type === a.assetType) || ASSET_TYPES[6];
                            return (
                                <div key={a.id} className={`p-4 rounded-xl border border-gray-100 group relative hover:shadow-md transition-all bg-gray-50/50`}>
                                    <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-all">
                                        <button onClick={() => { setEditItem(a); setShowAssetForm(true); }} className="p-1.5 bg-white rounded-lg text-gray-400 hover:text-blue-600 shadow-sm"><FaPen className="text-[9px]" /></button>
                                        <button onClick={() => handleDeleteAsset(a.id)} className="p-1.5 bg-white rounded-lg text-gray-400 hover:text-rose-500 shadow-sm"><FaTrash className="text-[9px]" /></button>
                                    </div>
                                    <div className={`w-9 h-9 ${at.color} rounded-lg flex items-center justify-center text-sm mb-3`}>{at.icon}</div>
                                    <p className="text-[10px] text-gray-400 uppercase tracking-widest mb-0.5">{a.assetType}</p>
                                    <p className={`text-sm font-semibold ${L.text}`}>{a.label}</p>
                                    <p className={`text-lg font-bold ${L.accent} mt-1`}>{'\u20B9'}{Number(a.value).toLocaleString()}</p>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Income Trend Chart */}
            <div className={L.card}>
                <h3 className={`text-base font-bold ${L.text} mb-4`}>Income Trend</h3>
                {trendData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={220}>
                        <AreaChart data={trendData}>
                            <defs>
                                <linearGradient id="incGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.2} />
                                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <XAxis dataKey="label" tick={{ fill: '#6B7280', fontSize: 10 }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fill: '#6B7280', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `\u20B9${(v / 1000).toFixed(0)}k`} />
                            <Tooltip formatter={(v) => `\u20B9${v.toLocaleString()}`} contentStyle={LIGHT_TOOLTIP} />
                            {dailyTarget > 0 && <ReferenceLine y={dailyTarget} stroke="#F59E0B" strokeDasharray="5 5" label={{ value: 'Target', fill: '#F59E0B', fontSize: 10 }} />}
                            <Area type="monotone" dataKey="income" stroke="#3B82F6" fill="url(#incGrad)" strokeWidth={2} />
                        </AreaChart>
                    </ResponsiveContainer>
                ) : (
                    <p className={`text-sm ${L.textMuted} text-center py-10`}>No income data for this period</p>
                )}
            </div>

            {/* Category Income Comparison */}
            {categoryData.length > 0 && (
                <div className={L.card}>
                    <h3 className={`text-base font-bold ${L.text} mb-4`}>Income by Category</h3>
                    <ResponsiveContainer width="100%" height={Math.max(180, Math.min(categoryData.length, 8) * 36)}>
                        <BarChart layout="vertical" data={categoryData.slice(0, 8)} margin={{ left: 10 }}>
                            <XAxis type="number" tick={{ fill: '#6B7280', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `\u20B9${(v / 1000).toFixed(0)}k`} />
                            <YAxis type="category" dataKey="name" tick={{ fill: '#6B7280', fontSize: 11 }} axisLine={false} tickLine={false} width={100} />
                            <Tooltip formatter={(v) => `\u20B9${v.toLocaleString()}`} contentStyle={LIGHT_TOOLTIP} />
                            <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                                {categoryData.slice(0, 8).map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}

            {/* Income Targets */}
            <div className={L.card}>
                <div className="flex items-center justify-between mb-5">
                    <div>
                        <h3 className={`text-base font-bold ${L.text}`}>Income Targets</h3>
                        <p className={`text-xs ${L.textMuted} mt-0.5`}>Set daily target, week & month auto-calculate</p>
                    </div>
                    <button onClick={() => setShowTargetForm(true)} className={`${L.btnSecondary} flex items-center gap-2`}>
                        <FaCog className="text-[10px]" /> Set Target
                    </button>
                </div>

                {dailyTarget > 0 ? (
                    <div className="space-y-5">
                        {/* Target Cards */}
                        <div className="grid grid-cols-3 gap-3">
                            {[
                                { label: 'Daily', target: dailyTarget, sub: null },
                                { label: 'Weekly', target: weeklyTarget, sub: weekHolidays > 0 ? `${7 - weekHolidays} working day${7 - weekHolidays !== 1 ? 's' : ''} (${weekHolidays} holiday${weekHolidays > 1 ? 's' : ''})` : null },
                                { label: 'Monthly', target: monthlyTarget, sub: monthHolidays > 0 ? `${daysInMonth - monthHolidays} working days (${monthHolidays} holiday${monthHolidays > 1 ? 's' : ''})` : `${daysInMonth} days` },
                            ].map(t => (
                                <div key={t.label} className="p-4 bg-gray-50 rounded-xl text-center">
                                    <p className="text-[10px] text-gray-400 uppercase tracking-widest">{t.label} Target</p>
                                    <p className={`text-lg font-bold ${L.text} mt-1`}>{'\u20B9'}{t.target.toLocaleString()}</p>
                                    {t.sub && <p className="text-[9px] text-gray-400 mt-1">{t.sub}</p>}
                                </div>
                            ))}
                        </div>

                        {/* Holidays */}
                        <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                    <FaUmbrellaBeach className="text-amber-500 text-sm" />
                                    <span className={`text-sm font-bold ${L.text}`}>Holidays</span>
                                    {monthHolidays > 0 && (
                                        <span className="bg-amber-100 text-amber-700 text-[10px] font-bold px-2 py-0.5 rounded-full">
                                            {monthHolidays} this month
                                        </span>
                                    )}
                                </div>
                                <button onClick={() => setShowHolidayForm(!showHolidayForm)}
                                    className="text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1 px-3 py-1.5 hover:bg-blue-50 rounded-lg transition-colors">
                                    <FaCalendarAlt className="text-[10px]" /> {showHolidayForm ? 'Cancel' : 'Add Holiday'}
                                </button>
                            </div>
                            {showHolidayForm && (
                                <div className="flex items-end gap-2 mb-3 p-3 bg-white rounded-lg border border-gray-100">
                                    <div className="flex-1">
                                        <label className="text-[10px] text-gray-400 uppercase tracking-widest mb-1 block">Date</label>
                                        <input type="date" value={holidayDate} onChange={e => setHolidayDate(e.target.value)} className={L.input} />
                                    </div>
                                    <div className="flex-1">
                                        <label className="text-[10px] text-gray-400 uppercase tracking-widest mb-1 block">Label (optional)</label>
                                        <input type="text" placeholder="e.g., Diwali" value={holidayLabel}
                                            onChange={e => setHolidayLabel(e.target.value)} className={L.input} />
                                    </div>
                                    <button onClick={handleAddHoliday} className={`${L.btnPrimary} whitespace-nowrap`}>Add</button>
                                </div>
                            )}
                            {monthHolidayList.length > 0 ? (
                                <div className="space-y-1.5">
                                    {monthHolidayList.map(h => (
                                        <div key={h.id} className="flex items-center justify-between px-3 py-2 bg-white rounded-lg border border-gray-100 group">
                                            <div className="flex items-center gap-2">
                                                <FaUmbrellaBeach className="text-amber-400 text-xs" />
                                                <span className="text-sm text-gray-700 font-medium">
                                                    {new Date(h.date + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}
                                                </span>
                                                <span className="text-xs text-gray-400">{h.label}</span>
                                            </div>
                                            <button onClick={() => handleDeleteHoliday(h.id)}
                                                className="p-1 text-gray-300 hover:text-rose-500 opacity-0 group-hover:opacity-100 transition-all">
                                                <FaTrash className="text-[10px]" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-xs text-gray-400 text-center py-2">No holidays declared this month</p>
                            )}
                        </div>

                        {/* Countdown */}
                        <div className="p-5 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-[10px] text-blue-400 uppercase tracking-widest">
                                        {calendarMode === 'day' ? 'Today' : calendarMode === 'week' ? 'This Week' : 'This Month'} Progress
                                    </p>
                                    <p className={`text-2xl font-bold ${targetPct >= 100 ? 'text-emerald-600' : 'text-blue-600'} mt-1`}>
                                        {Math.min(100, targetPct).toFixed(0)}%
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="text-[10px] text-gray-400 uppercase tracking-widest">Remaining</p>
                                    <p className={`text-xl font-bold ${targetRemaining <= 0 ? 'text-emerald-600' : 'text-amber-600'}`}>
                                        {targetRemaining <= 0 ? 'Achieved!' : `\u20B9${targetRemaining.toLocaleString()}`}
                                    </p>
                                </div>
                            </div>
                            <div className="h-2 bg-white rounded-full overflow-hidden mt-3">
                                <div className={`h-full rounded-full transition-all duration-700 ${targetPct >= 100 ? 'bg-emerald-500' : 'bg-blue-500'}`}
                                    style={{ width: `${Math.min(100, targetPct)}%` }} />
                            </div>
                        </div>

                        {/* Streak + Forecast */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="p-4 bg-orange-50 rounded-xl border border-orange-100 text-center">
                                <FaFire className="text-2xl text-orange-500 mx-auto mb-2" />
                                <p className="text-2xl font-bold text-orange-600">{incomeStreak}</p>
                                <p className="text-[10px] text-gray-500 uppercase tracking-widest mt-1">Day Streak</p>
                            </div>
                            {forecast !== null && (
                                <div className="p-4 bg-blue-50 rounded-xl border border-blue-100 text-center">
                                    <FaBullseye className="text-2xl text-blue-500 mx-auto mb-2" />
                                    <p className="text-2xl font-bold text-blue-600">{'\u20B9'}{forecast.toLocaleString()}</p>
                                    <p className="text-[10px] text-gray-500 uppercase tracking-widest mt-1">Required/Day</p>
                                </div>
                            )}
                        </div>

                        {/* Achievement Trend */}
                        {achievementData.length > 0 && (
                            <div>
                                <h4 className={`text-sm font-bold ${L.text} mb-3`}>Target Achievement (14 days)</h4>
                                <ResponsiveContainer width="100%" height={120}>
                                    <BarChart data={achievementData}>
                                        <XAxis dataKey="label" tick={{ fill: '#6B7280', fontSize: 9 }} axisLine={false} tickLine={false} />
                                        <Tooltip formatter={(v, name) => name === 'earned' ? `\u20B9${v.toLocaleString()}` : `\u20B9${v.toLocaleString()}`} contentStyle={LIGHT_TOOLTIP} />
                                        <Bar dataKey="earned" radius={[4, 4, 0, 0]}>
                                            {achievementData.map((d, i) => <Cell key={i} fill={d.isHoliday ? '#94A3B8' : d.hit ? '#10B981' : '#EF4444'} />)}
                                        </Bar>
                                        {dailyTarget > 0 && <ReferenceLine y={dailyTarget} stroke="#F59E0B" strokeDasharray="4 4" />}
                                    </BarChart>
                                </ResponsiveContainer>
                                <div className="flex items-center gap-4 mt-2">
                                    <span className="flex items-center gap-1.5 text-[10px] text-gray-500"><span className="w-2.5 h-2.5 rounded bg-emerald-500"></span> Target Hit</span>
                                    <span className="flex items-center gap-1.5 text-[10px] text-gray-500"><span className="w-2.5 h-2.5 rounded bg-rose-500"></span> Missed</span>
                                    <span className="flex items-center gap-1.5 text-[10px] text-gray-500"><span className="w-2.5 h-2.5 rounded bg-slate-400"></span> Holiday</span>
                                    <span className="flex items-center gap-1.5 text-[10px] text-gray-500"><span className="w-2.5 h-0.5 bg-amber-500"></span> Target Line</span>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="text-center py-8">
                        <FaBullseye className="text-3xl text-gray-300 mx-auto mb-3" />
                        <p className={`text-sm ${L.textMuted}`}>Set a daily income target to start tracking</p>
                        <button onClick={() => setShowTargetForm(true)} className={`${L.btnPrimary} mt-3`}>Set Target</button>
                    </div>
                )}
            </div>

            {/* Asset Form Modal */}
            {showAssetForm && (
                <div className={L.modal} onClick={() => { setShowAssetForm(false); setEditItem(null); }}>
                    <div className={L.modalCard} onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between">
                            <h3 className={`text-lg font-bold ${L.text}`}>{editItem ? 'Edit Asset' : 'Add Asset'}</h3>
                            <button onClick={() => { setShowAssetForm(false); setEditItem(null); }} className="p-2 text-gray-400 hover:text-gray-700"><FaTimes /></button>
                        </div>
                        <AssetForm L={L} item={editItem} onSave={async (data) => {
                            if (editItem) await updateInCollection('assets', editItem.id, data);
                            else await addToCollection('assets', data);
                            setShowAssetForm(false); setEditItem(null);
                        }} />
                    </div>
                </div>
            )}

            {/* Income Entry Form Modal */}
            {showIncomeForm && (
                <div className={L.modal} onClick={() => { setShowIncomeForm(false); setEditIncomeItem(null); }}>
                    <div className="bg-white w-full max-w-2xl rounded-2xl p-8 shadow-xl border border-gray-100 space-y-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between">
                            <h3 className={`text-lg font-bold ${L.text}`}>{editIncomeItem ? 'Edit Income' : 'Add Income'}</h3>
                            <button onClick={() => { setShowIncomeForm(false); setEditIncomeItem(null); }} className="p-2 text-gray-400 hover:text-gray-700"><FaTimes /></button>
                        </div>
                        <IncomeEntryForm L={L} item={editIncomeItem}
                            fixedExpenses={fixedExpenses} emis={emis} wishlistItems={wishlistItems}
                            onSave={async (data) => {
                                if (editIncomeItem) await updateInCollection('transactions', editIncomeItem.id, data);
                                else await addToCollection('transactions', data);
                                setShowIncomeForm(false); setEditIncomeItem(null);
                            }} />
                    </div>
                </div>
            )}

            {/* Income Target Modal */}
            {showTargetForm && (
                <div className={L.modal} onClick={() => setShowTargetForm(false)}>
                    <div className={L.modalCard} onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between">
                            <h3 className={`text-lg font-bold ${L.text}`}>Set Income Target</h3>
                            <button onClick={() => setShowTargetForm(false)} className="p-2 text-gray-400 hover:text-gray-700"><FaTimes /></button>
                        </div>
                        <IncomeTargetForm L={L} currentTarget={dailyTarget}
                            weekHolidays={weekHolidays} monthHolidays={monthHolidays} daysInMonth={daysInMonth}
                            onSave={async (val) => {
                                await updateIncomeTargets({ dailyTarget: Number(val) });
                                setShowTargetForm(false);
                            }} />
                    </div>
                </div>
            )}
        </div>
    );
};

// Forms
const AssetForm = ({ L, item, onSave }) => {
    const [form, setForm] = useState({ label: item?.label || '', value: item?.value || '', assetType: item?.assetType || 'Other' });
    const [saving, setSaving] = useState(false);
    const handleSubmit = async (e) => { e.preventDefault(); setSaving(true); await onSave({ ...form, value: Number(form.value) }); setSaving(false); };
    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <input type="text" placeholder="Asset name" value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} required className={L.input} />
            <input type="number" placeholder="Current value" value={form.value} onChange={e => setForm({ ...form, value: e.target.value })} required className={L.input} />
            <select value={form.assetType} onChange={e => setForm({ ...form, assetType: e.target.value })} className={L.input}>
                {ASSET_TYPES.map(t => <option key={t.type} value={t.type}>{t.type}</option>)}
            </select>
            <button type="submit" disabled={saving} className={`w-full py-3 ${L.btnPrimary}`}>{saving ? 'Saving...' : item ? 'Update' : 'Add Asset'}</button>
        </form>
    );
};

const IncomeEntryForm = ({ L, item, onSave, fixedExpenses, emis, wishlistItems }) => {
    const [form, setForm] = useState({
        description: item?.description || item?.label || '',
        amount: item?.amount ? Math.abs(item.amount) : '',
        category: item?.category || 'Salary',
        date: item?.date ? new Date(item.date).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10),
    });
    const [saving, setSaving] = useState(false);
    const [allocations, setAllocations] = useState(item?.allocations || []);
    const [allocExpType, setAllocExpType] = useState('fixed');
    const [allocItemId, setAllocItemId] = useState('');
    const [allocAmount, setAllocAmount] = useState('');

    // Items for selected expense type
    const expenseItems = useMemo(() => {
        if (allocExpType === 'fixed') return (fixedExpenses || []).filter(e => e.isActive !== false).map(e => ({ id: e.id, label: e.label, amount: Number(e.amount) || 0 }));
        if (allocExpType === 'emi') return (emis || []).map(e => ({ id: e.id, label: e.label, amount: Number(e.monthlyAmount) || 0 }));
        return (wishlistItems || []).filter(i => i.status !== 'completed').map(i => ({ id: i.id, label: i.name, amount: Math.max(0, (Number(i.amount) || 0) - (Number(i.totalPaid) || 0)) }));
    }, [allocExpType, fixedExpenses, emis, wishlistItems]);

    // Auto-fill amount when item changes
    const handleItemSelect = (id) => {
        setAllocItemId(id);
        const found = expenseItems.find(e => e.id === id);
        setAllocAmount(found ? String(found.amount) : '');
    };

    const totalAllocated = allocations.reduce((s, a) => s + (Number(a.amount) || 0), 0);
    const remaining = (Number(form.amount) || 0) - totalAllocated;
    const overAllocated = remaining < 0;

    const handleAddAllocation = () => {
        if (!allocItemId || !allocAmount || Number(allocAmount) <= 0) return;
        const found = expenseItems.find(e => e.id === allocItemId);
        if (!found) return;
        const newAlloc = {
            type: allocExpType,
            itemId: allocItemId,
            itemLabel: found.label,
            itemTotal: found.amount,
            amount: Number(allocAmount),
            status: 'unpaid',
        };
        setAllocations(prev => [...prev, newAlloc]);
        setAllocItemId('');
        setAllocAmount('');
    };

    const handleRemoveAllocation = (idx) => {
        setAllocations(prev => prev.filter((_, i) => i !== idx));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        await onSave({
            type: 'income',
            description: form.description,
            label: form.description,
            amount: Number(form.amount),
            category: form.category,
            date: new Date(form.date + 'T12:00:00').toISOString(),
            allocations: allocations.length > 0 ? allocations : [],
        });
        setSaving(false);
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div>
                <label className="text-[10px] text-gray-400 uppercase tracking-widest mb-1 block">Description</label>
                <input type="text" placeholder="e.g., Salary, Freelance payment" value={form.description}
                    onChange={e => setForm({ ...form, description: e.target.value })} required className={L.input} />
            </div>
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className="text-[10px] text-gray-400 uppercase tracking-widest mb-1 block">Amount</label>
                    <input type="number" placeholder="e.g., 50000" value={form.amount}
                        onChange={e => setForm({ ...form, amount: e.target.value })} required className={L.input} />
                </div>
                <div>
                    <label className="text-[10px] text-gray-400 uppercase tracking-widest mb-1 block">Date</label>
                    <input type="date" value={form.date}
                        onChange={e => setForm({ ...form, date: e.target.value })} className={L.input} />
                </div>
            </div>
            <div>
                <label className="text-[10px] text-gray-400 uppercase tracking-widest mb-1 block">Category</label>
                <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className={L.input}>
                    {INCOME_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
            </div>

            {/* Allocate Income Section */}
            {Number(form.amount) > 0 && (
                <div className="border-t border-gray-100 pt-4 space-y-3">
                    <p className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold">Allocate Income</p>

                    {/* Add allocation row */}
                    <div className="grid grid-cols-12 gap-2 items-end">
                        <div className="col-span-3">
                            <label className="text-[10px] text-gray-400 mb-1 block">Type</label>
                            <select value={allocExpType} onChange={e => { setAllocExpType(e.target.value); setAllocItemId(''); setAllocAmount(''); }} className={L.input}>
                                <option value="fixed">Fixed</option>
                                <option value="emi">EMI</option>
                                <option value="onetime">One-Time</option>
                            </select>
                        </div>
                        <div className="col-span-4">
                            <label className="text-[10px] text-gray-400 mb-1 block">Item</label>
                            <select value={allocItemId} onChange={e => handleItemSelect(e.target.value)} className={L.input}>
                                <option value="">Select...</option>
                                {expenseItems.map(e => <option key={e.id} value={e.id}>{e.label} ({'\u20B9'}{e.amount.toLocaleString()})</option>)}
                            </select>
                        </div>
                        <div className="col-span-3">
                            <label className="text-[10px] text-gray-400 mb-1 block">Amount</label>
                            <input type="number" value={allocAmount} onChange={e => setAllocAmount(e.target.value)}
                                placeholder="0" className={L.input} />
                        </div>
                        <div className="col-span-2">
                            <button type="button" onClick={handleAddAllocation}
                                disabled={!allocItemId || !allocAmount || Number(allocAmount) <= 0}
                                className={`w-full py-3 text-sm font-bold rounded-xl transition-all ${
                                    allocItemId && allocAmount && Number(allocAmount) > 0
                                        ? 'bg-blue-500 text-white hover:bg-blue-600'
                                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                }`}>+ Add</button>
                        </div>
                    </div>

                    {/* Coverage preview */}
                    {allocItemId && allocAmount > 0 && (() => {
                        const found = expenseItems.find(e => e.id === allocItemId);
                        if (!found || found.amount <= 0) return null;
                        const pct = ((Number(allocAmount) / found.amount) * 100).toFixed(0);
                        return (
                            <p className="text-[10px] text-blue-500">
                                Covers {pct}% of {found.label} ({'\u20B9'}{found.amount.toLocaleString()})
                            </p>
                        );
                    })()}

                    {/* Allocation list */}
                    {allocations.length > 0 && (
                        <div className="space-y-1.5">
                            {allocations.map((alloc, idx) => (
                                <div key={idx} className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg text-sm">
                                    <div className="flex items-center gap-2">
                                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${
                                            alloc.type === 'fixed' ? 'bg-blue-100 text-blue-700' :
                                            alloc.type === 'emi' ? 'bg-pink-100 text-pink-700' :
                                            'bg-indigo-100 text-indigo-700'
                                        }`}>{alloc.type}</span>
                                        <span className="text-gray-700 font-medium">{alloc.itemLabel}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="font-semibold text-gray-700">{'\u20B9'}{Number(alloc.amount).toLocaleString()}</span>
                                        <span className="text-gray-400 text-xs">
                                            ({alloc.itemTotal > 0 ? ((alloc.amount / alloc.itemTotal) * 100).toFixed(0) : 0}%)
                                        </span>
                                        <button type="button" onClick={() => handleRemoveAllocation(idx)}
                                            className="p-1 text-gray-400 hover:text-rose-500"><FaTimes className="text-[9px]" /></button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Summary bar */}
                    <div className={`flex items-center justify-between px-4 py-3 rounded-xl text-sm font-semibold ${
                        overAllocated ? 'bg-rose-50 text-rose-600 border border-rose-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                    }`}>
                        <span>Allocated: {'\u20B9'}{totalAllocated.toLocaleString()}</span>
                        <span>{overAllocated ? `Over by \u20B9${Math.abs(remaining).toLocaleString()}` : `\u2192 Savings: \u20B9${remaining.toLocaleString()}`}</span>
                    </div>
                </div>
            )}

            <button type="submit" disabled={saving || overAllocated} className={`w-full py-3 ${L.btnPrimary} ${overAllocated ? 'opacity-50 cursor-not-allowed' : ''}`}>
                {saving ? 'Saving...' : item ? 'Update Income' : 'Add Income'}
            </button>
        </form>
    );
};

const IncomeTargetForm = ({ L, currentTarget, onSave, weekHolidays = 0, monthHolidays = 0, daysInMonth = 30 }) => {
    const [value, setValue] = useState(currentTarget || '');
    const [saving, setSaving] = useState(false);
    const handleSubmit = async (e) => { e.preventDefault(); setSaving(true); await onSave(value); setSaving(false); };
    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <p className={`text-xs ${L.textMuted}`}>Set your daily income target. Weekly and monthly will auto-calculate.</p>
            <div>
                <label className="text-[10px] text-gray-400 uppercase tracking-widest mb-1 block">Daily Income Target</label>
                <input type="number" placeholder="e.g., 15000" value={value} onChange={e => setValue(e.target.value)} className={L.input} />
            </div>
            {value > 0 && (
                <div className="grid grid-cols-2 gap-3 p-3 bg-gray-50 rounded-xl">
                    <div className="text-center">
                        <p className="text-[10px] text-gray-400 uppercase">Weekly</p>
                        <p className={`text-sm font-bold ${L.text}`}>{'\u20B9'}{(Number(value) * (7 - weekHolidays)).toLocaleString()}</p>
                        {weekHolidays > 0 && <p className="text-[9px] text-amber-500 mt-0.5">{weekHolidays} holiday{weekHolidays > 1 ? 's' : ''} excl.</p>}
                    </div>
                    <div className="text-center">
                        <p className="text-[10px] text-gray-400 uppercase">Monthly</p>
                        <p className={`text-sm font-bold ${L.text}`}>{'\u20B9'}{(Number(value) * (daysInMonth - monthHolidays)).toLocaleString()}</p>
                        {monthHolidays > 0 && <p className="text-[9px] text-amber-500 mt-0.5">{monthHolidays} holiday{monthHolidays > 1 ? 's' : ''} excl.</p>}
                    </div>
                </div>
            )}
            <button type="submit" disabled={saving} className={`w-full py-3 ${L.btnPrimary}`}>{saving ? 'Saving...' : 'Save Target'}</button>
        </form>
    );
};

export default IncomeTab;
